import React, { type RefObject, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Avatar, { AvatarItem } from '@atlaskit/avatar';
import Badge from '@atlaskit/badge';
import EditorOpenIcon from '@atlaskit/icon/core/migration/arrow-up-right--editor-open';
import QuestionCircleIcon from '@atlaskit/icon/core/migration/question-circle';
import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';
import { Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	useAnalytics,
	useTeamCentralStore,
} from '@atlassian/ptc-common';
import { useReportingLinesStore } from '@atlassian/teams';

import { useReportingLinesContext } from '../ReportingLinesContext';
import { useReportingLinesStore as DEPRECATED_useReportingLinesStore } from '../store';

import { messages } from './messages';
import * as Styled from './styles';
import { AccountIdentifierType, type ReportingLinesUser } from './types';

const textWithTooltip = (text: string) => {
	return (
		<Tooltip content={text}>
			<Styled.AvatarItemText>{text}</Styled.AvatarItemText>
		</Tooltip>
	);
};

export interface ReportingLinesUserCardProps {
	user: ReportingLinesUser;
	selected?: boolean;
	testId?: string;
}

export interface DirectReportsBadgeProps {
	numberOfDirectReports: number;
	isSelected: boolean;
}

export interface TotalReportsBadgeProps {
	numberOfDirectReports: number;
	numberOfTotalReports: number;
	isSelected: boolean;
}

const AvatarItemContainer = ({ children }: { children: React.ReactNode }) => (
	<Styled.AvatarItemContainer>{children}</Styled.AvatarItemContainer>
);

const TotalReportsBadge = (props: TotalReportsBadgeProps) => {
	const intl = useIntl();
	return props.numberOfTotalReports > 0 && props.numberOfDirectReports > 0 ? (
		<Tooltip
			content={
				props.numberOfTotalReports <= props.numberOfDirectReports
					? intl.formatMessage(messages.DirectReportCountBadgeTooltip, {
							directReportCount: intl.formatNumber(props.numberOfDirectReports),
						})
					: intl.formatMessage(messages.TotalReportCountBadgeTooltip, {
							totalReportCount: intl.formatNumber(props.numberOfTotalReports),
							directReportBreakdownText: intl.formatMessage(messages.DirectReportBreakdownText, {
								directReportCount: intl.formatNumber(props.numberOfDirectReports),
							}),
						})
			}
		>
			<Badge appearance={props.isSelected ? 'primary' : 'default'} max={99999}>
				{props.numberOfTotalReports}
			</Badge>
		</Tooltip>
	) : null;
};

export const ReportingLinesUserCard = (props: ReportingLinesUserCardProps) => {
	const { WrappedWithProfilecard } = useReportingLinesContext();
	const userAaid = props?.user?.accountIdentifier;

	return userAaid && WrappedWithProfilecard ? (
		<WrappedWithProfilecard userId={userAaid} position="right">
			<ReportingLinesUserContent {...props} />
		</WrappedWithProfilecard>
	) : (
		<span data-testid={props.testId}>
			<ReportingLinesUserContent {...props} />
		</span>
	);
};

const ReportingLinesUserContent = ({ user, selected = false }: ReportingLinesUserCardProps) => {
	const analytics = useAnalytics();
	const { fireEvent } = useAnalyticsEvents();
	const { product = '' } = useReportingLinesContext();
	const [teamCentralStore] = useTeamCentralStore();
	const [, { selectUser }] = useReportingLinesStore();
	const [, { selectUser: selectUserDEPRECATED }] = DEPRECATED_useReportingLinesStore();

	if (user.identifierType !== AccountIdentifierType.ATLASSIAN_ID || !user.pii?.name) {
		return (
			<MissingProfileCard
				numMissingProfiles={1}
				selected={selected}
				data-testid="missing-user-card"
			/>
		);
	}

	const { getActiveWorkspaceId } = teamCentralStore.teamCentralHandlers;
	const workspaceUuid = getActiveWorkspaceId ? getActiveWorkspaceId() : null;

	const name = user.pii?.name ?? undefined;
	const picture = user.pii?.picture ?? undefined;
	const jobTitle = user.pii?.extendedProfile?.jobTitle;

	const label = `${name}${jobTitle ? ` (${jobTitle})` : ''}`;

	return (
		<Styled.ReportingLinesChartCard
			selected={selected}
			onClick={(e) => {
				if (fg('ptc-enable-ptc-common-analytics-refactor')) {
					fireEvent('ui.ReportingLinesUserCard.clicked', {
						product,
						workspaceUuid,
					});
				} else {
					analytics.pushUIEvent({
						source: AnalyticsEventSource.USER_PROFILE_SCREEN,
						actionSubject: 'ReportingLinesUserCard',
						action: AnalyticsEventActions.CLICKED,
						tags: ['watermelon'],
						attributes: { product, workspaceUuid },
					});
				}
				e.preventDefault();
				if (user.accountIdentifier) {
					if (fg('ptc-consume-reporting-lines')) {
						selectUser(user.accountIdentifier);
					} else {
						selectUserDEPRECATED(user.accountIdentifier);
					}
				}
				return false;
			}}
			testId="user-card"
		>
			<AvatarItemContainer>
				<AvatarItem
					avatar={<Avatar name={name} src={picture} label={label} />}
					primaryText={name && <Styled.AvatarItemText>{name}</Styled.AvatarItemText>}
					secondaryText={jobTitle && textWithTooltip(jobTitle)}
					label={label}
				/>
			</AvatarItemContainer>
			<TotalReportsBadge
				numberOfDirectReports={user.numberOfDirectReports}
				numberOfTotalReports={user.numberOfTotalReports}
				isSelected={selected}
			/>
		</Styled.ReportingLinesChartCard>
	);
};
interface MissingProfileCardProps {
	numMissingProfiles: number;
	selected?: boolean;
	testId?: string;
}

export const MissingProfileCard = ({
	numMissingProfiles,
	selected = false,
	testId,
}: MissingProfileCardProps) => {
	const [isHoveringCard, setIsHoveringCard] = useState(false);
	const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);

	const intl = useIntl();

	const name = intl.formatMessage(messages.MissingProfileName, {
		numMissingProfiles,
	});

	const helpText = intl.formatMessage(messages.MissingProfileTooltip, {
		name,
		numMissingProfiles,
	});

	const helpLink = intl.formatMessage(messages.MissingProfileTooltipGuide);

	const tooltipContent = (
		<Styled.TooltipWrapper>
			<Styled.TooltipContainer
				onMouseEnter={() => setIsHoveringTooltip(true)}
				onMouseLeave={() => setIsHoveringTooltip(false)}
			>
				<Text as="p">{helpText}</Text>
				<Text as="p">
					<Link
						href="https://intercom.help/atlas-by-atlassian/en/articles/5572925-how-to-sync-the-manager-attribute-into-atlas#h_2e7f6754d1"
						rel="noopener noreferrer"
						target="_blank"
					>
						{helpLink}
						<EditorOpenIcon spacing="spacious" color="currentColor" label={helpLink} />
					</Link>
				</Text>
			</Styled.TooltipContainer>
		</Styled.TooltipWrapper>
	);

	return (
		<Popup
			testId={testId}
			content={() => tooltipContent}
			isOpen={isHoveringCard || isHoveringTooltip}
			placement="bottom"
			onClose={() => {
				setIsHoveringCard(false);
				setIsHoveringTooltip(false);
			}}
			autoFocus={false}
			trigger={(triggerProps) => {
				const { ref, ...innerProps } = triggerProps;
				return (
					<div ref={ref as RefObject<HTMLDivElement>} {...innerProps}>
						<Styled.ReportingLinesChartCard selected={selected} disabled>
							<AvatarItemContainer>
								<AvatarItem
									avatar={<Avatar />}
									secondaryText={
										<Styled.MissingProfileCardStyle>{name}</Styled.MissingProfileCardStyle>
									}
								/>
							</AvatarItemContainer>
							<div
								// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
								onMouseEnter={() => setIsHoveringCard(true)}
								// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
								onMouseLeave={() => setTimeout(() => setIsHoveringCard(false), 500)}
							>
								<QuestionCircleIcon label={helpText} color={token('color.icon.subtle', N500)} />
							</div>
						</Styled.ReportingLinesChartCard>
					</div>
				);
			}}
		/>
	);
};
